import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BadgeCheck, User, Tags, ToggleLeft, Calendar, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

// Vencimentos de Registros e Certificados (WVCTM01 / RVCTM01 do ERP) — validades da vctmot no período

interface Certificado { motcod: string; nome: string; vct: number; descricao: string; numero: string; validade: string }

const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function RelatorioVencimentoCertificados() {
  const { empresaCod } = useEmpresa()
  const hoje = isoLocal(new Date())
  const [funcCod, setFuncCod] = useState('')
  const [funcNome, setFuncNome] = useState('')
  const [tiposOpcoes, setTiposOpcoes] = useState<{ cod: string; des: string }[]>([])
  const [tiposSel, setTiposSel] = useState<string[]>([])
  const [busca] = useSearchParams() // o cartão do dashboard abre com período, só ativos, sem afastados e já gerado
  const dataUrl = (k: string) => (/^\d{4}-\d{2}-\d{2}$/.test(busca.get(k) ?? '') ? busca.get(k)! : '')
  const [listagem, setListagem] = useState(busca.get('ativos') === '1' ? 'A' : '')
  const [semAfastados, setSemAfastados] = useState(busca.get('semAfastados') === '1')
  const [dataIni, setDataIni] = useState(dataUrl('inicio') || hoje)
  const [dataFim, setDataFim] = useState(dataUrl('fim') || hoje)
  const [res, setRes] = useState<Certificado[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/certificados-registros')
      .then(r => setTiposOpcoes(r.data.lista.map((c: any) => ({ cod: String(c.vctcod), des: `${c.vctcod} — ${c.vctdes}` }))))
      .catch(() => setTiposOpcoes([]))
  }, [])

  const params = () => ({ motcod: funcCod, tipos: tiposSel.join(','), listagem, inicio: dataIni, fim: dataFim, semAfastados: semAfastados ? 1 : 0, empresa: empresaCod ?? 1 })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/vencimento-certificados', { params: params() })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => { if (busca.get('auto') === '1') gerar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get('/funcionarios/relatorio/vencimento-certificados', { params: { ...params(), formato }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'vencimentos-certificados.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      let msg = formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.'
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setGerando('')
    }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'left', whiteSpace: 'nowrap' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <BadgeCheck size={18} color="#fff" />
            </span>
            <div>
              <h2>Vencimentos de Registros e Certificados</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Registros e certificados dos funcionários vencendo no período</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Funcionário" icon={User}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ minWidth: 300 }}>
                <LookupSelect endpoint="/funcionarios" extraParams={{ status: 'todos' }} codKey="motcod" descKey="motnome" tagKey="motcod"
                  codValue={funcCod} descValue={funcNome}
                  onSelect={(cod, desc) => { setFuncCod(cod); setFuncNome(desc) }}
                  placeholder="Todos" listWidth={320} />
              </div>
              {funcCod
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => { setFuncCod(''); setFuncNome('') }}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>

          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>

          <Linha label="Tipos" icon={Tags}>
            <SeletorMultiplo opcoes={tiposOpcoes} selecionados={tiposSel} onChange={setTiposSel} placeholderBusca="Buscar registro/certificado..." />
          </Linha>

          <Linha label="Listagem" icon={ToggleLeft}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <select value={listagem} onChange={e => setListagem(e.target.value)} style={{ maxWidth: 160 }}>
                <option value="">Todos</option>
                <option value="A">Ativos</option>
                <option value="I">Inativos</option>
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={semAfastados} onChange={e => { setSemAfastados(e.target.checked); setRes(null) }} />
                Sem quem está afastado hoje
              </label>
            </div>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {res.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum registro/certificado vencendo no período.</div>}

            {res.length > 0 && (
              <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Código</th><th style={th}>Funcionário</th><th style={{ ...th, textAlign: 'right' }}>Tipo</th>
                      <th style={th}>Registro/Certificado</th><th style={th}>Número</th><th style={th}>Validade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.map((l, i) => {
                      const vencido = !!l.validade && l.validade < hoje
                      return (
                        <tr key={`${l.motcod}-${l.vct}`} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, fontWeight: 600 }}>{l.motcod}</td>
                          <td style={{ ...td, fontWeight: 600 }}>{l.nome}</td>
                          <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.vct}</td>
                          <td style={td}>{l.descricao}</td>
                          <td style={td}>{l.numero}</td>
                          <td style={{ ...td, fontWeight: 700, color: vencido ? '#b91c1c' : '#1b3f7b' }}>{dataBr(l.validade)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em' }}>
              TOTAL DE REGISTROS: {res.length}
            </div>
            {res.length > 0 && <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>Validade em vermelho = já vencido.</div>}
          </div>
        </div>
      )}
    </div>
  )
}
